import React from 'react';

/**
 * Native checkbox that handles react-table's tri-state {@code indeterminate}
 * flag correctly.
 *
 * <p>react-table's {@code getToggleAllRowsSelectedProps()} /
 * {@code row.getToggleRowSelectedProps()} return a props object that includes
 * an {@code indeterminate: boolean} field. Spreading that directly onto a
 * native {@code <input>} triggers React's
 * <code>"Received `false` for a non-boolean attribute `indeterminate`"</code>
 * warning, because {@code indeterminate} is not a real HTML attribute —
 * it's a JavaScript property on {@code HTMLInputElement} that must be set
 * via the DOM, not via JSX attributes.
 *
 * <p>This wrapper extracts {@code indeterminate} out of the spread props and
 * assigns it to the input element via {@code useEffect}, so the warning goes
 * away and the visual "some-rows-selected" dash still appears.
 */
type Props = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  indeterminate?: boolean;
};

const IndeterminateCheckbox = React.forwardRef<HTMLInputElement, Props>(
  ({ indeterminate, ...rest }, ref) => {
    const defaultRef = React.useRef<HTMLInputElement>(null);
    const resolvedRef =
      (ref as React.MutableRefObject<HTMLInputElement | null>) ?? defaultRef;

    React.useEffect(() => {
      if (resolvedRef.current) {
        resolvedRef.current.indeterminate = Boolean(indeterminate);
      }
    }, [resolvedRef, indeterminate]);

    return <input type="checkbox" ref={resolvedRef} {...rest} />;
  }
);

IndeterminateCheckbox.displayName = 'IndeterminateCheckbox';

export default IndeterminateCheckbox;
